<script lang="ts">
  import { Button, Form, Input, type Language } from '@bruce/components'
  import { STRINGS } from '$lib/constants/strings'
  import { schema } from '../../routes/[lang]/(auth)/signup/schema'
  import Turnstile from '$lib/components/Turnstile.svelte'
  import { page } from '$app/stores'
  import type { RenderParameters } from 'turnstile-types'
  import type { CityId } from '@bruce/api/common/city'
  import SocialAuthButton from './SocialAuthButton.svelte'
  import type { SocialCallbackState } from '$lib/types/socialCallbackState'
  import { utils } from '$lib/utils'
  import type { ContractType } from '$lib/types/contract'

  export let inviteCode: string | undefined = undefined
  export let trial: boolean | undefined = undefined
  export let turnstileAppearance: RenderParameters['appearance'] = 'always'
  export let cityId: CityId
  export let giftCard: boolean | undefined = undefined

  let marketingOptIn = false
  let disabled = true

  $: lang = $page.params.lang as Language
  $: tier = $page.url.searchParams.get('tier')
  $: contractType =
    $page.url.searchParams.get('contract-type') === 'annual'
      ? 'annual'
      : ('monthly' as ContractType)
  $: localeRoute = utils.getRoute(lang)

  $: continueText = getContinueText(tier, contractType)

  $: socialState = JSON.stringify({
    code: inviteCode,
    search: trial ? '?trial=true&tier=black' : $page.url.search,
    context: 'signup',
    city: cityId,
    marketing_opt_in: marketingOptIn,
  } satisfies SocialCallbackState)

  function getContinueText(tier: string | null, contractType: ContractType) {
    let text: string = STRINGS[lang].SIGNUP_CONTINUE

    if (tier) {
      const contractTypeText =
        contractType === 'annual' ? STRINGS[lang].ANNUAL : STRINGS[lang].MONTHLY
      text = `${text} ${STRINGS[lang].SIGNUP_WITH} ${utils.toTitleCase(tier)} ${contractTypeText}`
    }

    return text
  }
</script>

<div class="sign-up-container">
  <Form
    schema={schema(lang)}
    action={localeRoute(
      `/signup${trial ? '?trial=true&tier=black' : $page.url.search}`,
    )}
  >
    <section class="sign-up">
      {#if inviteCode}
        <Input
          label={STRINGS[lang].SIGNUP_INVITE_CODE}
          type="text"
          name="code"
          variant="outlined"
          rounded
          block
          required
          value={inviteCode}
        />
      {/if}
      <Input
        label={giftCard
          ? STRINGS[lang].SIGNUP_YOUR_EMAIL
          : STRINGS[lang].SIGNUP_EMAIL}
        type="email"
        name="email"
        variant="outlined"
        rounded
        block
        autocomplete="email"
        autofocus
        required
      />
      <Input
        label={giftCard
          ? STRINGS[lang].SIGNUP_YOUR_FIRST_NAME
          : STRINGS[lang].SIGNUP_FIRST_NAME}
        type="text"
        name="first_name"
        variant="outlined"
        rounded
        block
        autocomplete="given-name"
      />
      <Input
        label={giftCard
          ? STRINGS[lang].SIGNUP_YOUR_LAST_NAME
          : STRINGS[lang].SIGNUP_LAST_NAME}
        type="text"
        name="last_name"
        variant="outlined"
        rounded
        block
        autocomplete="family-name"
      />
      <Input
        label={STRINGS[lang].SIGNUP_PASSWORD}
        type="password"
        name="password"
        variant="outlined"
        rounded
        block
        autocomplete="current-password"
      />
      <Input type="number" hidden name="city_id" value={cityId} />
      <div class="terms">
        <Input type="checkbox" required>
          <div slot="label">
            {STRINGS[lang].SIGNUP_AGREE[0]}
            <a href={localeRoute('/terms')} target="_blank"
              >{STRINGS[lang].SIGNUP_TERMS}</a
            >
            {STRINGS[lang].SIGNUP_AGREE[1]}
            <a href={localeRoute('/privacy')} target="_blank"
              >{STRINGS[lang].SIGNUP_PRIVACY_POLICY}</a
            >.
          </div>
        </Input>
        <Input
          type="checkbox"
          name="marketing_opt_in"
          checked={marketingOptIn}
          on:click={() => (marketingOptIn = !marketingOptIn)}
        >
          <div slot="label">
            {STRINGS[lang].SIGNUP_MARKETING_OPT_IN}
            <a href={localeRoute('/marketing')} target="_blank"
              >{STRINGS[lang].SIGNUP_MARKETING_OPT_IN_MORE_INFO}</a
            >.
          </div>
        </Input>
      </div>
      <Turnstile
        appearance={turnstileAppearance}
        on:turnstile-callback={() => (disabled = false)}
      />
      <Button
        type="submit"
        rounded
        block
        {disabled}
        loading={turnstileAppearance === 'interaction-only' && disabled}
      >
        {continueText}
      </Button>
    </section>
  </Form>
</div>
<section class="social">
  <p>{STRINGS[lang].SIGNUP_OR_WITH_SOCIAL}</p>
  <div class="buttons">
    <SocialAuthButton social="apple" context="signup" state={socialState} />
    <SocialAuthButton social="google" context="signup" state={socialState} />
  </div>
</section>
<section class="already-have-an-account">
  {#if inviteCode}
    <Button variant="text" href={localeRoute('/app')} rounded>
      {STRINGS[lang].SIGNUP_APP}
    </Button>
  {:else}
    <Button
      variant="text"
      href={localeRoute(
        trial ? '/login?trial=true&tier=black' : `/login${$page.url.search}`,
      )}
      rounded
    >
      {STRINGS[lang].SIGNUP_LOGIN}
    </Button>
  {/if}
</section>

<style lang="scss">
  .sign-up-container {
    :global(form) {
      margin-top: 1em;
    }

    .sign-up {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1em;

      .terms {
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        gap: 1em;
      }
    }
  }

  .social {
    p {
      font-size: 16px;
      text-align: center;
    }

    .buttons {
      display: flex;
      justify-content: center;
      gap: 1em;
    }
  }

  .already-have-an-account {
    display: flex;
    justify-content: center;
    margin-top: 1em;
  }
</style>
